import React, { useState, useEffect, useRef } from 'react';
import { baseUrl } from '../../shared';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const MAKES = ['CATERPILLAR', 'DURAPAC', 'ENERPAC', 'OTHER'];

const EMPTY_FORM = { make: '', type: '', model: '', capacityKg: '', compressedHeightMm: '', strokeMm: '', extendedHeightMm: '', notes: '' };

function CylinderForm({ open, onClose, initial, onSaved, canManage }) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setForm(initial ? { ...initial } : { ...EMPTY_FORM });
  }, [open, initial]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.make || !form.model) { setError('Make and Model are required.'); return; }
    setSaving(true); setError('');
    try {
      const method = initial?.id ? 'PUT' : 'POST';
      const url = baseUrl + 'workshop/cylinders' + (initial?.id ? '/' + initial.id : '');
      const r = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error('Save failed');
      onSaved();
    } catch (e) { setError('Save failed. Please try again.'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial?.id ? 'Edit Cylinder' : 'Add Cylinder'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Make</InputLabel>
              <Select value={form.make} label="Make" onChange={set('make')}>
                {MAKES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="Type / Series" value={form.type} onChange={set('type')} sx={{ flex: 1 }} />
          </Box>
          <TextField size="small" label="Model *" value={form.model} onChange={set('model')} fullWidth />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField size="small" label="Capacity (kg)" value={form.capacityKg} onChange={set('capacityKg')} type="number" sx={{ flex: 1 }} />
            <TextField size="small" label="Compressed Height (mm)" value={form.compressedHeightMm} onChange={set('compressedHeightMm')} type="number" sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField size="small" label="Stroke (mm)" value={form.strokeMm} onChange={set('strokeMm')} type="number" sx={{ flex: 1 }} />
            <TextField size="small" label="Extended Height (mm)" value={form.extendedHeightMm} onChange={set('extendedHeightMm')} type="number" sx={{ flex: 1 }} />
          </Box>
          <TextField size="small" label="Notes" value={form.notes} onChange={set('notes')} fullWidth multiline rows={2} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({ open, onClose, cylinder, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const confirm = async () => {
    setDeleting(true);
    await fetch(baseUrl + 'workshop/cylinders/' + cylinder.id, {
      method: 'DELETE', headers: authHeaders(),
    });
    setDeleting(false);
    onDeleted();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>Delete Cylinder</DialogTitle>
      <DialogContent>
        <Typography>Delete <strong>{cylinder?.make} {cylinder?.model}</strong>? This cannot be undone.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={deleting}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CylinderReference() {
  const navigate = useNavigate();
  const canManage = localStorage.getItem('canManageWorkshop') === '1';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [makeFilter, setMakeFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importStatus, setImportStatus] = useState('');
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (makeFilter) params.set('make', makeFilter);
    fetch(baseUrl + 'workshop/cylinders?' + params, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search, makeFilter]);

  const handleEdit = (row) => { setEditing(row); setFormOpen(true); };
  const handleNew = () => { setEditing(null); setFormOpen(true); };

  // CSV import: expected columns: make,type,model,capacityKg,compressedHeightMm,strokeMm,extendedHeightMm,notes
  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) { setImportStatus('CSV appears empty.'); return; }
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const cylinders = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = vals[idx] || ''; });
      if (obj.make || obj.model) cylinders.push(obj);
    }
    if (cylinders.length === 0) { setImportStatus('No valid rows found.'); return; }
    setImportStatus('Importing ' + cylinders.length + ' rows…');
    const r = await fetch(baseUrl + 'workshop/cylinders/bulk', {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ cylinders }),
    });
    const data = await r.json();
    setImportStatus('Imported ' + (data.inserted || 0) + ' cylinders.');
    load();
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/workshop')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Hydraulic Cylinder Reference</Typography>
          <Typography variant="body2" color="text.secondary">Capacity, stroke, and height data for lift planning</Typography>
        </Box>
        {canManage && (
          <>
            <Tooltip title="Import from CSV (columns: make,type,model,capacityKg,compressedHeightMm,strokeMm,extendedHeightMm,notes)">
              <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => fileRef.current.click()}>
                Import CSV
              </Button>
            </Tooltip>
            <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvImport} />
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleNew}>
              Add Cylinder
            </Button>
          </>
        )}
      </Box>

      {importStatus && (
        <Alert severity="info" onClose={() => setImportStatus('')} sx={{ mb: 2 }}>{importStatus}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" label="Search make / type / model" value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 280 }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Make</InputLabel>
          <Select value={makeFilter} label="Filter by Make" onChange={e => setMakeFilter(e.target.value)}>
            <MenuItem value=""><em>All Makes</em></MenuItem>
            {MAKES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {rows.length} {rows.length === 1 ? 'result' : 'results'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Make</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type / Series</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Capacity (kg)</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Compressed Ht (mm)</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Stroke (mm)</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Extended Ht (mm)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                {canManage && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No cylinders found. {canManage ? 'Add one or import from CSV.' : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.make}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.model}</TableCell>
                  <TableCell align="right">{row.capacityKg != null ? Number(row.capacityKg).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">{row.compressedHeightMm ?? '—'}</TableCell>
                  <TableCell align="right">{row.strokeMm ?? '—'}</TableCell>
                  <TableCell align="right">{row.extendedHeightMm ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200, color: 'text.secondary', fontSize: 12 }}>{row.notes}</TableCell>
                  {canManage && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CylinderForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editing}
        canManage={canManage}
        onSaved={() => { setFormOpen(false); load(); }}
      />

      {deleteTarget && (
        <DeleteDialog
          open
          cylinder={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(); }}
        />
      )}
    </Box>
  );
}
